import Logger from '@editor/services/Logger/Logger'

import { Module, PipelineStats, Polyline, PolylineExtractionConfig } from './interfaces'

/**
 * Main polyline extraction pipeline class
 * Coordinates preprocessing, extraction, and postprocessing stages
 */
export class PolylineExtraction extends Module {
  private config: PolylineExtractionConfig

  constructor(config: PolylineExtractionConfig, logger?: Logger) {
    super()
    this.logger = logger
    this.config = config

    // Go through each module and set logger
    if (this.logger) {
      this.config.pre.forEach((module) => {
        module.setLogger(this.logger!)
      })
      this.config.extraction.setLogger(this.logger)
      this.config.post.forEach((module) => {
        module.setLogger(this.logger!)
      })
    }
  }

  /**
   * Extract polylines from point cloud data
   * @param points - Input point cloud as interleaved Float32Array [x,y,z,x,y,z,...]
   * @returns Object containing extracted polylines and execution statistics
   */
  extract(points: Float32Array): { polylines: Polyline[]; stats: PipelineStats } {
    this.logger?.log('🔍 [PolylineExtraction] Starting modular polyline extraction pipeline...')

    const startTime = performance.now()
    const inputPointCount = points.length / 3

    // Validate input
    if (!points || points.length === 0) {
      throw new Error('Input points array is empty')
    }
    if (points.length % 3 !== 0) {
      throw new Error('Points array length must be divisible by 3 (x,y,z coordinates)')
    }

    this.logger?.log(`📊 [PolylineExtraction] Processing ${inputPointCount} input points`)

    // Initialize stats
    const stats: PipelineStats = {
      totalTime: 0,
      inputPoints: inputPointCount,
      outputPolylines: 0,
      stages: {
        preprocessing: [],
        extraction: { module: '', time: 0, pointsIn: 0, polylinesOut: 0 },
        postprocessing: [],
      },
    }

    // Run pipeline stages
    const processedPoints = this.runPreprocessing(points, stats)
    const extractedPolylines = this.runExtraction(processedPoints, stats)
    const finalPolylines = this.runPostprocessing(extractedPolylines, stats)

    // Finalize stats
    const totalTime = performance.now() - startTime
    stats.totalTime = totalTime
    stats.outputPolylines = finalPolylines.length

    this.logger?.log(
      `🎉 [PolylineExtraction] Pipeline complete: ${inputPointCount} points → ${finalPolylines.length} polylines in ${totalTime.toFixed(1)}ms`,
    )
    finalPolylines.forEach((polyline, index) => {
      this.logger?.log(`  Polyline ${index + 1}: ${polyline.length} points`)
    })

    return { polylines: finalPolylines, stats }
  }

  /**
   * Run preprocessing stage
   * @param points - Input points
   * @param stats - Pipeline statistics to update
   * @returns Processed points
   */
  runPreprocessing(points: Float32Array, stats: PipelineStats): Float32Array {
    this.logger?.log(`🔧 [PolylineExtraction] Running ${this.config.pre.length} preprocessing modules...`)

    let currentPoints = points

    this.config.pre.forEach((module) => {
      const startTime = performance.now()
      const inputCount = currentPoints.length / 3

      this.logger?.log(`  📋 Running ${module.getName()}... (${inputCount} points)`)

      currentPoints = module.run(currentPoints)

      const endTime = performance.now()
      const outputCount = currentPoints.length / 3
      const moduleTime = endTime - startTime

      stats.stages.preprocessing.push({
        module: module.getName(),
        time: moduleTime,
        pointsIn: inputCount,
        pointsOut: outputCount,
      })

      this.logger?.log(`    ✅ ${module.getName()}: ${inputCount} → ${outputCount} points (${moduleTime.toFixed(1)}ms)`)
    })

    const finalCount = currentPoints.length / 3
    this.logger?.log(`✅ [PolylineExtraction] Preprocessing complete: ${points.length / 3} → ${finalCount} points`)

    return currentPoints
  }

  /**
   * Run extraction stage
   * @param points - Preprocessed points
   * @param stats - Pipeline statistics to update
   * @returns Extracted polylines
   */
  runExtraction(points: Float32Array, stats: PipelineStats): Polyline[] {
    this.logger?.log(`📏 [PolylineExtraction] Running extraction module: ${this.config.extraction.getName()}...`)

    const startTime = performance.now()
    const inputCount = points.length / 3

    const polylines = this.config.extraction.run(points)

    const endTime = performance.now()
    const moduleTime = endTime - startTime

    stats.stages.extraction = {
      module: this.config.extraction.getName(),
      time: moduleTime,
      pointsIn: inputCount,
      polylinesOut: polylines.length,
    }

    this.logger?.log(
      `✅ [PolylineExtraction] Extraction complete: ${inputCount} points → ${polylines.length} polylines (${moduleTime.toFixed(1)}ms)`,
    )

    return polylines
  }

  /**
   * Run postprocessing stage
   * @param polylines - Extracted polylines
   * @param stats - Pipeline statistics to update
   * @returns Processed polylines
   */
  runPostprocessing(polylines: Polyline[], stats: PipelineStats): Polyline[] {
    this.logger?.log(`🔧 [PolylineExtraction] Running ${this.config.post.length} postprocessing modules...`)

    let currentPolylines = polylines

    this.config.post.forEach((module) => {
      const startTime = performance.now()
      const inputCount = currentPolylines.length

      this.logger?.log(`  📋 Running ${module.getName()}... (${inputCount} polylines)`)

      currentPolylines = module.run(currentPolylines)

      const endTime = performance.now()
      const outputCount = currentPolylines.length
      const moduleTime = endTime - startTime

      stats.stages.postprocessing.push({
        module: module.getName(),
        time: moduleTime,
        polylinesIn: inputCount,
        polylinesOut: outputCount,
      })

      this.logger?.log(
        `    ✅ ${module.getName()}: ${inputCount} → ${outputCount} polylines (${moduleTime.toFixed(1)}ms)`,
      )
    })

    const finalCount = currentPolylines.length
    this.logger?.log(`✅ [PolylineExtraction] Postprocessing complete: ${polylines.length} → ${finalCount} polylines`)

    return currentPolylines
  }

  /**
   * Get pipeline configuration summary
   */
  getConfigSummary(): string {
    const preNames = this.config.pre.map((m) => m.getName()).join(' → ')
    const postNames = this.config.post.map((m) => m.getName()).join(' → ')

    return `Pipeline: [${preNames || 'None'}] → ${this.config.extraction.getName()} → [${postNames || 'None'}]`
  }
}
